import {
  CustomTableCell,
  CustomTableHead,
  FormCheckbox,
  FormFileInput,
  FormSectionHeader,
  FormSelect,
  Loader,
} from "@/components";
import { Badge, Button, Card, CardContent, Table, TableBody, TableHeader, TableRow } from "@/components/ui";
import { EyeIcon, Trash2 } from "lucide-react";
import {
  CLOSING_ACK,
  OWNERSHIP_PROOF_DOCS,
  FILE_SIZE,
  FILE_TYPE,
  IS_REQUIRED,
  PROOF_FILE,
  PROOF_TYPE,
  UPLOADED_STATUS,
  uploadStatusStyle,
} from "../../consts";
import { StepsProps } from "../types";
import useStep4 from "./useStep4";
import { FormProvider } from "react-hook-form";
import { cn } from "@/lib/utils";

const Step4 = ({ onCancel, onSubmit: onBaseSubmit, isView, isAddPending }: StepsProps) => {
  const {
    form,
    fileInputRefs,
    proofDocs,
    onRemoveProof,
    onAddMore,
    isDocListLoading,
    ocpDocList,
    formData,
    onViewAttachment,
    onSubmit,
    isAddDocPending,
    onSubmitClick,
    updatedKey,
    isBillPending,
    disabledSaveNext
  } = useStep4({
    onBaseSubmit
  });
  const { errors } = form.formState;
  
  return (
    <>
      {isDocListLoading ? <Loader /> : 
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Card className="rounded-lg border-0 shadow-none">
              <CardContent>
                <FormSectionHeader className="text-[15px] font-semibold text-g-700 flex flex-col">
                  <span>Ownership Change Documents</span>
                  <span className="text-[12px] text-gray-400 font-normal">Proof types are populated from Master Data.</span>
                </FormSectionHeader>

                <div className="overflow-x-auto w-full mt-5">
                  <div className="min-w-[1000px]">
                    <Table>
                      <TableHeader>
                        <TableRow className="mb-3 rounded">
                          <CustomTableHead className="w-[150px]">
                            Proof Type
                          </CustomTableHead>
                          <CustomTableHead className="w-[150px]">
                            Proof File
                          </CustomTableHead>
                          <CustomTableHead className="w-[150px]">
                            Size
                          </CustomTableHead>
                          <CustomTableHead className="w-[150px]">
                            Status
                          </CustomTableHead>
                          <CustomTableHead className="w-[150px]">
                            Action
                          </CustomTableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {proofDocs?.length ? proofDocs?.map((_: any, index: number) => {
                          const file = formData?.[OWNERSHIP_PROOF_DOCS]?.[index]?.[PROOF_FILE];
                          const isUploaded = formData?.[OWNERSHIP_PROOF_DOCS]?.[index]?.[UPLOADED_STATUS] && !(formData?.[OWNERSHIP_PROOF_DOCS]?.[index]?.[PROOF_FILE] instanceof File);
                          return (
                            <TableRow
                              key={`row-${index}-${_?.[PROOF_TYPE]}`}
                              onClick={(e) => {
                                // e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <CustomTableCell>
                                <div className="flex flex-row items-center gap-3">
                                  <FormSelect
                                    name={`${OWNERSHIP_PROOF_DOCS}.${index}.${PROOF_TYPE}`}
                                    control={form.control}
                                    label=""
                                    options={ocpDocList?.map((data: any) => ({
                                      label: data?.documentName,
                                      value: data?.documentTypeId?.toString(),
                                      disabled: data?.requirementType == "REQUIRED",
                                    }))}
                                    placeholder="Please select"
                                    error={
                                      (errors as any)?.[OWNERSHIP_PROOF_DOCS]?.[index]?.[PROOF_TYPE]?.message as string
                                    }
                                    isRequired={formData?.[OWNERSHIP_PROOF_DOCS]?.[index]?.[IS_REQUIRED]}
                                    disabled={isView || formData[OWNERSHIP_PROOF_DOCS][index]?.[IS_REQUIRED]}
                                  />
                                  {formData[OWNERSHIP_PROOF_DOCS][index]?.[IS_REQUIRED] ? <Badge
                                    variant="outline"
                                    className={cn(
                                      "bg-amber-50 border-amber-600 border text-amber-600",
                                      "rounded-full px-2 py-1"
                                    )}
                                  >
                                    {"Required"}
                                  </Badge> : <Badge
                                    variant="outline"
                                    className={cn(
                                      "bg-gray-50 border-black border text-black",
                                      "rounded-full px-2 py-1"
                                    )}
                                  >
                                    {"Optional"}
                                  </Badge>}
                                </div>
                              </CustomTableCell>
                              <CustomTableCell>
                                <FormFileInput
                                  key={`${updatedKey}-${index}`}
                                  fileInputRef={fileInputRefs.current[index]}
                                  name={`${OWNERSHIP_PROOF_DOCS}.${index}.${PROOF_FILE}`}
                                  control={form.control}
                                  label=""
                                  placeholder="Upload File"
                                  errors={
                                    (errors as any)?.[OWNERSHIP_PROOF_DOCS]?.[index]?.[PROOF_FILE]?.message as string
                                  }
                                  isRequired={formData[OWNERSHIP_PROOF_DOCS][index]?.[IS_REQUIRED]}
                                  disabled={isView}
                                  accept={formData[OWNERSHIP_PROOF_DOCS][index]?.[FILE_TYPE]}
                                  maxSize={formData[OWNERSHIP_PROOF_DOCS][index]?.[FILE_SIZE]}
                                />
                              </CustomTableCell>
                              <CustomTableCell>
                                <div className="flex items-center justify-center">{formData?.[OWNERSHIP_PROOF_DOCS]?.[index]?.[PROOF_FILE]?.size ? `${parseFloat((formData?.[OWNERSHIP_PROOF_DOCS]?.[index]?.[PROOF_FILE]?.size / (1024 * 1024)).toString()).toFixed(2)} MB` : "-"} </div>
                              </CustomTableCell>
                              <CustomTableCell>
                                {!!file ? <div className="flex items-center justify-center">
                                  <Badge
                                    variant="outline"
                                    className={cn(
                                      uploadStatusStyle[isUploaded ? "uploaded" : "pending"],
                                      "rounded-full px-4 py-2"
                                    )}
                                  >
                                    {isUploaded ? "Uploaded" : "Pending"}
                                  </Badge>
                                </div> : <></>}
                              </CustomTableCell>
                              <CustomTableCell>
                                <div className="flex items-center justify-center">
                                  {!!file ? (
                                    <Button
                                      variant="link"
                                      type="button"
                                      onClick={() => onViewAttachment(
                                        file?.url ? file?.url : 
                                        URL.createObjectURL(file)
                                      )}
                                    >
                                      <EyeIcon className="h-5 w-5" /> View
                                    </Button>
                                  ) : <></>}
                                  {!formData[OWNERSHIP_PROOF_DOCS][index]?.[IS_REQUIRED] && (
                                    <div className="flex items-center">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={onRemoveProof(index)}
                                        disabled={isView}
                                      >
                                        <Trash2 className="h-6 w-6 stroke-red-600" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              </CustomTableCell>
                            </TableRow>
                          );
                        }) : <></>}
                      </TableBody>
                    </Table>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onAddMore}
                  disabled={isView}
                  className="mt-4 text-primary hover:bg-transparent hover:text-primary"
                >
                  + Add More
                </Button>
                {isBillPending ? <FormCheckbox
                  name={`${CLOSING_ACK}`}
                  control={form.control}
                  label="I certify that I have cleared all pending bills."
                  placeholder="Please enter"
                  error={
                    (errors as any)?.[CLOSING_ACK]?.message as string
                  }
                  isRequired={isBillPending}
                  disabled={isView}
                /> : <></>}
              </CardContent>
            </Card>
            <div className="mt-7 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                className="min-w-[110px]"
                onClick={onCancel}
                disabled={isAddPending}
              >
                Cancel
              </Button>
              {isView ? <></> :
              <>
                <Button className="min-w-[90px]" disabled={isAddPending || isAddDocPending || disabledSaveNext} onClick={() => onSubmitClick("save")}>
                  {isAddPending ? <Loader variant="secondary" /> : <></>} Save & Next
                </Button>
              </>}
            </div>
          </form>
        </FormProvider>
      }
    </>
  );
};

export default Step4;